import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Header';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';


const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyReports: true,
    monthlyReports: true,
    transactionAlerts: true,
    darkMode: false,
    autoSave: true,
    twoFactorAuth: false
  });

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev?.[setting]
    }));
  };

  const handleSaveSettings = () => {
    localStorage.setItem('userSettings', JSON.stringify(settings));
    alert('Configurações salvas com sucesso!');
  };

  const settingsSections = [
    {
      title: 'Notificações',
      icon: 'Bell',
      settings: [
        { key: 'emailNotifications', label: 'Notificações por E-mail', description: 'Receba atualizações importantes por e-mail' },
        { key: 'pushNotifications', label: 'Notificações Push', description: 'Receba notificações no navegador' },
        { key: 'transactionAlerts', label: 'Alertas de Transações', description: 'Seja notificado sobre movimentações financeiras' }
      ]
    },
    {
      title: 'Relatórios',
      icon: 'FileText',
      settings: [
        { key: 'weeklyReports', label: 'Relatórios Semanais', description: 'Receba resumos semanais automaticamente' },
        { key: 'monthlyReports', label: 'Relatórios Mensais', description: 'Receba relatórios mensais detalhados' }
      ]
    },
    {
      title: 'Preferências do Sistema',
      icon: 'Settings',
      settings: [
        { key: 'darkMode', label: 'Modo Escuro', description: 'Ativar tema escuro da interface' },
        { key: 'autoSave', label: 'Salvamento Automático', description: 'Salvar alterações automaticamente' }
      ]
    },
    {
      title: 'Segurança',
      icon: 'Shield',
      settings: [
        { key: 'twoFactorAuth', label: 'Autenticação de Dois Fatores', description: 'Adicionar camada extra de segurança' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Voltar</span>
          </button>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg">
                <Icon name="Settings" size={32} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configurações</h1>
                <p className="text-gray-600 dark:text-gray-400">Gerencie as preferências do seu sistema</p>
              </div>
            </div>
          </div>

          {settingsSections?.map((section, index) => (
            <div key={index} className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Icon name={section?.icon} size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{section?.title}</h2>
              </div>

              <div className="space-y-4">
                {section?.settings?.map((setting, settingIndex) => (
                  <div
                    key={settingIndex}
                    className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <label className="text-base font-medium text-gray-900 dark:text-white cursor-pointer">
                          {setting?.label}
                        </label>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {setting?.description}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handleToggle(setting?.key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings?.[setting?.key]
                            ? 'bg-blue-600' :'bg-gray-300 dark:bg-slate-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings?.[setting?.key] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="Database" size={20} className="text-blue-600" />
              Gerenciamento de Dados
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Gerencie seus dados e preferências de privacidade
            </p>
            <div className="flex gap-4">
              <Button className="bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white">
                <Icon name="Download" size={18} className="mr-2" />
                Exportar Dados
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Icon name="Trash2" size={18} className="mr-2" />
                Limpar Cache
              </Button>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSaveSettings}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              <Icon name="Save" size={18} className="mr-2" />
              Salvar Configurações
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;